import { CalendarInfo } from "../types"
import {
  customer_select_options,
  existing_customer_options,
  existing_retailer_options,
  merge_account_names,
  use_promotion_account_options,
} from "../account_options"
import { SearchableSelectField } from "./form_fields"

/** Option value that clears the (optional) retailer. */
const NO_RETAILER_OPTION = ""

interface PromotionAccountFieldsProps {
  customer: string
  retailer: string
  onCustomerChange: (customer: string) => void
  onRetailerChange: (retailer: string) => void
  /** Calendars whose own retailer and loaded promotions add to the org-wide lists. */
  calendars: CalendarInfo[]
  /** Show the one-line explanation under each field (off where the form explains both itself). */
  showHelp?: boolean
  /** Set when the form sits inside a Dialog. */
  insideModal?: boolean
  /** Outline the customer in red: it is required and missing, after the rep tried to save. */
  customerError?: boolean
}

function to_options(names: string[]): { value: string; label: string }[] {
  return names.map((name: string) => ({ value: name, label: name }))
}

function add_customer_label(text: string): string {
  return `Add "${text}" as a new customer`
}

function add_retailer_label(text: string): string {
  return `Add "${text}" as a new retailer`
}

/**
 * The Customer (Distributor) and Retailer (Key Account) pickers every promotion form shares: two
 * searchable dropdowns over the org's known names, each able to take a name that is not listed yet.
 * Renders the two fields side by side as siblings — put it inside the form's own grid.
 *
 * A name added here is stored on the promotion as typed. It is not created in the accounting
 * system and is not linked to an accounting customer.
 */
export function PromotionAccountFields({
  customer,
  retailer,
  onCustomerChange,
  onRetailerChange,
  calendars,
  showHelp = true,
  insideModal,
  customerError = false,
}: PromotionAccountFieldsProps) {
  const account_options = use_promotion_account_options()
  // The form's own value goes first so its spelling is the one kept (see merge_account_names).
  const customer_names = merge_account_names([customer], existing_customer_options(calendars), account_options?.customers)
  const retailer_names = merge_account_names([retailer], existing_retailer_options(calendars), account_options?.retailers)

  return (
    <>
      <SearchableSelectField
        label="Customer (Distributor)"
        value={customer}
        onChange={onCustomerChange}
        onCreateOption={onCustomerChange}
        createOptionLabel={add_customer_label}
        options={customer_select_options(customer_names)}
        placeholder="Select a customer"
        searchPlaceholder="Search customers, or type a new one..."
        emptyMessage="No matching customers. Type a name to add it."
        help={
          showHelp
            ? 'The distributor that buys from you and bills you for the deal (KeHE, UNFI…). If the retailer buys from you directly, pick "Direct" and choose the retailer next to it.'
            : undefined
        }
        insideModal={insideModal}
        error={customerError}
        id="promotion-customer"
        required
      />
      <SearchableSelectField
        label="Retailer (Key Account)"
        value={retailer}
        onChange={onRetailerChange}
        onCreateOption={onRetailerChange}
        createOptionLabel={add_retailer_label}
        options={[{ value: NO_RETAILER_OPTION, label: "No retailer" }, ...to_options(retailer_names)]}
        placeholder="Select a retailer"
        searchPlaceholder="Search retailers, or type a new one..."
        emptyMessage="No matching retailers. Type a name to add it."
        help={showHelp ? "Where shoppers buy the product, e.g. Whole Foods or Target." : undefined}
        insideModal={insideModal}
      />
    </>
  )
}
